import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { apiClient } from "@/App";
import { format, addDays, isBefore, startOfToday } from "date-fns";
import { getSlotsForDate } from "@/lib/availability";
import { CalendarIcon, User, Mail, Phone, Stethoscope, Clock, FileText, CheckCircle2, Loader2, ArrowLeft } from "lucide-react";
import { cn } from "@/lib/utils";
import Footer from "@/components/Footer";

const LOGO_URL = "/logo.svg";

const BookingPage = () => {
  const navigate = useNavigate();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [bookingRef, setBookingRef] = useState("");
  
  const [formData, setFormData] = useState({
    patient_name: "",
    patient_email: "",
    patient_phone: "",
    appointment_type: "",
    preferred_date: null,
    preferred_time: "",
    notes: "",
    custom_fields: {}
  });

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await apiClient.get("/patient/settings");
        setSettings(response.data);
      } catch (error) {
        console.error("Failed to fetch settings", error);
        setSettings({
          appointment_types: ["General Consultation", "Specialist Visit", "Lab Tests"],
          time_slots: [
            "08:00 AM", "08:30 AM", "09:00 AM", "09:30 AM", "10:00 AM",
            "10:30 AM", "11:00 AM", "11:30 AM", "12:00 PM", "12:30 PM",
            "02:00 PM", "02:30 PM", "03:00 PM", "03:30 PM", "04:00 PM",
            "04:30 PM", "05:00 PM"
          ],
          custom_fields: []
        });
      }
    };
    fetchSettings();
  }, []);

  const availableTimeSlots = getSlotsForDate(settings, formData.preferred_date);

  useEffect(() => {
    if (formData.preferred_time && !availableTimeSlots.includes(formData.preferred_time)) {
      setFormData((current) => ({ ...current, preferred_time: "" }));
    }
  }, [availableTimeSlots, formData.preferred_time]);

  const updateCustomField = (fieldId, value) => {
    setFormData({
      ...formData,
      custom_fields: {
        ...formData.custom_fields,
        [fieldId]: value
      }
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await apiClient.post("/appointments", {
        ...formData,
        preferred_date: format(formData.preferred_date, "yyyy-MM-dd")
      });
      
      setBookingRef(response.data.id.substring(0, 8).toUpperCase());
      setSubmitted(true);
      toast.success("Appointment booked successfully!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Booking failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-teal-50 via-white to-teal-50 flex flex-col">
        <div className="flex-1 flex items-center justify-center p-4">
          <Card className="w-full max-w-md shadow-xl border-0 bg-white/90 backdrop-blur-sm">
            <CardContent className="pt-8 pb-8 text-center space-y-6">
              <div className="mx-auto w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center">
                <CheckCircle2 className="h-8 w-8 text-teal-600" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2" data-testid="success-title">
                  Appointment Confirmed!
                </h2>
                <p className="text-gray-600">
                  Thank you for booking with Jubail Specialist Hospital
                </p>
              </div>
              <div className="bg-teal-50 rounded-lg p-4">
                <p className="text-sm text-gray-600 mb-1">Your Reference Number</p>
                <p className="text-2xl font-mono font-bold text-teal-700" data-testid="booking-reference">
                  {bookingRef}
                </p>
              </div>
              <p className="text-sm text-gray-500">
                A confirmation email with a calendar invitation has been sent to your email address.
                Please arrive 15 minutes before your scheduled time.
              </p>
              <Button
                onClick={() => {
                  setSubmitted(false);
                  setFormData({
                    patient_name: "",
                    patient_email: "",
                    patient_phone: "",
                    appointment_type: "",
                    preferred_date: null,
                    preferred_time: "",
                    notes: ""
                  });
                }}
                variant="outline"
                className="w-full border-teal-200 text-teal-600 hover:bg-teal-50"
                data-testid="book-another-btn"
              >
                Book Another Appointment
              </Button>
            </CardContent>
          </Card>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-white to-teal-50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-sm border-b border-gray-100 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <img
            src={LOGO_URL}
            alt="Jubail Specialist Hospital"
            className="h-12 w-auto"
            data-testid="booking-logo"
          />
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate("/admin")}
            className="text-gray-600 hover:text-teal-600"
            data-testid="admin-login-link"
          >
            <ArrowLeft className="h-4 w-4 mr-1" />
            Admin Login
          </Button>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 py-8">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2" data-testid="booking-title">
            Book Your Appointment
          </h1>
          <p className="text-gray-600">
            Schedule a visit with our healthcare professionals
          </p>
        </div>

        <Card className="shadow-xl border-0 bg-white/90 backdrop-blur-sm">
          <CardHeader className="border-b border-gray-100 bg-gradient-to-r from-teal-600 to-teal-700 text-white rounded-t-lg">
            <CardTitle className="text-xl">Appointment Details</CardTitle>
            <CardDescription className="text-teal-100">
              Please fill in your information to book an appointment
            </CardDescription>
          </CardHeader>
          <CardContent className="p-6">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Personal Information */}
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name" className="flex items-center gap-2 text-gray-700">
                    <User className="h-4 w-4 text-teal-600" />
                    Full Name
                  </Label>
                  <Input
                    id="name"
                    placeholder="Enter your full name"
                    value={formData.patient_name}
                    onChange={(e) => setFormData({ ...formData, patient_name: e.target.value })}
                    className="border-gray-200 focus:border-teal-500 focus:ring-teal-500"
                    required
                    data-testid="patient-name-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email" className="flex items-center gap-2 text-gray-700">
                    <Mail className="h-4 w-4 text-teal-600" />
                    Email Address
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="your@email.com"
                    value={formData.patient_email}
                    onChange={(e) => setFormData({ ...formData, patient_email: e.target.value })}
                    className="border-gray-200 focus:border-teal-500 focus:ring-teal-500"
                    required
                    data-testid="patient-email-input"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="phone" className="flex items-center gap-2 text-gray-700">
                    <Phone className="h-4 w-4 text-teal-600" />
                    Phone Number
                  </Label>
                  <Input
                    id="phone"
                    type="tel"
                    placeholder="+233 XX XXX XXXX"
                    value={formData.patient_phone}
                    onChange={(e) => setFormData({ ...formData, patient_phone: e.target.value })}
                    className="border-gray-200 focus:border-teal-500 focus:ring-teal-500"
                    required
                    data-testid="patient-phone-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="flex items-center gap-2 text-gray-700">
                    <Stethoscope className="h-4 w-4 text-teal-600" />
                    Appointment Type
                  </Label>
                  <Select
                    value={formData.appointment_type}
                    onValueChange={(value) => setFormData({ ...formData, appointment_type: value })}
                    required
                  >
                    <SelectTrigger className="border-gray-200 focus:ring-teal-500" data-testid="appointment-type-select">
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      {settings?.appointment_types?.map((type) => (
                        <SelectItem key={type} value={type}>
                          {type}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Date and Time */}
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="flex items-center gap-2 text-gray-700">
                    <CalendarIcon className="h-4 w-4 text-teal-600" />
                    Preferred Date
                  </Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          "w-full justify-start text-left font-normal border-gray-200",
                          !formData.preferred_date && "text-gray-500"
                        )}
                        data-testid="date-picker-trigger"
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {formData.preferred_date ? (
                          format(formData.preferred_date, "PPP")
                        ) : (
                          "Pick a date"
                        )}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={formData.preferred_date}
                        onSelect={(date) => setFormData({ ...formData, preferred_date: date })}
                        disabled={(date) => isBefore(date, startOfToday())}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                <div className="space-y-2">
                  <Label className="flex items-center gap-2 text-gray-700">
                    <Clock className="h-4 w-4 text-teal-600" />
                    Preferred Time
                  </Label>
                  <Select
                    value={formData.preferred_time}
                    onValueChange={(value) => setFormData({ ...formData, preferred_time: value })}
                    required
                    disabled={!formData.preferred_date || availableTimeSlots.length === 0}
                  >
                    <SelectTrigger className="border-gray-200 focus:ring-teal-500" data-testid="time-select">
                      <SelectValue placeholder={formData.preferred_date ? "Select time" : "Pick a date first"} />
                    </SelectTrigger>
                    <SelectContent>
                      {availableTimeSlots.map((slot) => (
                        <SelectItem key={slot} value={slot}>
                          {slot}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {formData.preferred_date && availableTimeSlots.length === 0 && (
                    <p className="text-sm text-amber-600">
                      No appointment slots are available for the selected day.
                    </p>
                  )}
                </div>
              </div>

              {/* Notes */}
              <div className="space-y-2">
                <Label htmlFor="notes" className="flex items-center gap-2 text-gray-700">
                  <FileText className="h-4 w-4 text-teal-600" />
                  Additional Notes (Optional)
                </Label>
                <Textarea
                  id="notes"
                  placeholder="Any specific concerns or information you'd like us to know..."
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  className="border-gray-200 focus:border-teal-500 focus:ring-teal-500 min-h-[100px]"
                  data-testid="notes-input"
                />
              </div>

              {/* Custom Fields */}
              {settings?.custom_fields?.length > 0 && (
                <div className="space-y-4 pt-4 border-t border-gray-100">
                  <p className="text-sm font-medium text-gray-700">Additional Information</p>
                  {settings.custom_fields.map((field) => (
                    <div key={field.id} className="space-y-2">
                      <Label htmlFor={field.id} className="text-gray-700">
                        {field.label} {field.required && <span className="text-red-500">*</span>}
                      </Label>
                      {field.field_type === "textarea" ? (
                        <Textarea
                          id={field.id}
                          placeholder={field.placeholder || ""}
                          value={formData.custom_fields[field.id] || ""}
                          onChange={(e) => updateCustomField(field.id, e.target.value)}
                          required={field.required}
                          className="border-gray-200 focus:border-teal-500 focus:ring-teal-500"
                        />
                      ) : field.field_type === "select" ? (
                        <Select
                          value={formData.custom_fields[field.id] || ""}
                          onValueChange={(value) => updateCustomField(field.id, value)}
                          required={field.required}
                        >
                          <SelectTrigger className="border-gray-200">
                            <SelectValue placeholder={field.placeholder || "Select..."} />
                          </SelectTrigger>
                          <SelectContent>
                            {(field.options || []).map((option) => (
                              <SelectItem key={option} value={option}>
                                {option}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : (
                        <Input
                          id={field.id}
                          type={field.field_type === "email" ? "email" : field.field_type === "phone" ? "tel" : field.field_type === "number" ? "number" : "text"}
                          placeholder={field.placeholder || ""}
                          value={formData.custom_fields[field.id] || ""}
                          onChange={(e) => updateCustomField(field.id, e.target.value)}
                          required={field.required}
                          className="border-gray-200 focus:border-teal-500 focus:ring-teal-500"
                        />
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Submit Button */}
              <Button
                type="submit"
                className="w-full h-12 bg-teal-600 hover:bg-teal-700 text-white font-medium text-lg transition-all duration-200"
                disabled={loading || !formData.patient_name || !formData.patient_email || !formData.patient_phone || !formData.appointment_type || !formData.preferred_date || !formData.preferred_time}
                data-testid="submit-booking-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                    Booking...
                  </>
                ) : (
                  "Confirm Appointment"
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Contact Info */}
        <div className="mt-8 text-center text-sm text-gray-500">
          <p className="mb-2">
            Need assistance? Contact us at{" "}
            <a href="tel:+233303401167" className="text-teal-600 hover:underline">
              +233 30 340 1167
            </a>
          </p>
          <p>
            Pentecost - Jubail Street, Sakumono, Greater Accra, Ghana
          </p>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default BookingPage;
